// Lo schema dei parametri di una connessione «API» (tela, artboard «Aggiungi API — parametri»):
// un interruttore apre una textarea JSON, una chiave per parametro con una descrizione o uno
// schema (type, enum, default). Nessun valore è fisso: i valori li sceglie il pop. Il controllo
// vero lo fa il server; qui si dice subito se il JSON si legge e quali parametri contiene.
import { useState } from 'react';
import { Button } from '../../components/Button';
import { Field, TextArea } from '../../components/Field';
import { Icon } from '../../components/Icon';

export const PARAMS_EXAMPLE = `{
  "sort": { "enum": ["published", "updated"], "description": "Ordinamento" },
  "per_page": { "type": "integer", "default": 30 },
  "ecosystem": "npm, pip, maven…"
}`;

export type ParamsCheck = { names: string[] } | { error: string };

/** Il JSON si legge? Torna i nomi dei parametri, o l'errore da mostrare. */
export function checkParams(text: string): ParamsCheck {
  if (!text.trim()) return { names: [] };
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch (e) {
    return { error: `JSON non valido: ${e instanceof Error ? e.message : 'controlla il testo'}` };
  }
  if (!data || typeof data !== 'object' || Array.isArray(data))
    return { error: 'Serve un oggetto JSON: una chiave per parametro.' };
  const obj = data as Record<string, unknown>;
  const props = obj.type === 'object' && obj.properties ? obj.properties : obj;
  return { names: Object.keys(props as Record<string, unknown>) };
}

/** `value` null: interruttore spento, nessuno schema. */
export function ParamsField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const on = value !== null;
  const check = on ? checkParams(value) : null;
  return (
    <div className="params-box">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        className="params-switch"
        onClick={() => onChange(on ? null : PARAMS_EXAMPLE)}
      >
        <span style={{ flex: 1, display: 'grid', gap: 2 }}>
          <span style={{ font: '800 15px/20px var(--font-ui)' }}>Parametri</span>
          <span className="conn-desc">
            Descrivi quali parametri accetta il servizio: il pop li vede e sceglie i valori a ogni
            chiamata.
          </span>
        </span>
        <span className="pop-switch" aria-hidden="true" data-on={on || undefined}>
          <span />
        </span>
      </button>
      {on && (
        <Field
          label="Schema dei parametri (JSON)"
          hint="Un parametro per chiave. Il valore è una descrizione, oppure uno schema JSON con type, enum, default. Nessun valore è fisso: decide il pop."
        >
          <TextArea value={value} onChange={onChange} rows={8} mono />
        </Field>
      )}
      {check && 'error' in check && (
        <div className="params-note" data-bad="">
          <Icon name="octagon-alert" size={18} />
          {check.error}
        </div>
      )}
      {check && 'names' in check && check.names.length > 0 && (
        <div className="params-note">
          <Icon name="check" size={18} />
          {check.names.length} {check.names.length === 1 ? 'parametro' : 'parametri'}:{' '}
          {check.names.join(', ')}
        </div>
      )}
    </div>
  );
}

/** Pronto da salvare: spento, oppure acceso con un JSON che si legge. */
export function paramsReady(value: string | null): boolean {
  return value === null || !('error' in checkParams(value));
}

/** Nella scheda della connessione: i parametri descritti e il loro editor. */
export function ParamsLine({ value, onSave }: { value: string; onSave: (text: string) => void }) {
  const [draft, setDraft] = useState<string | null | undefined>(undefined);
  const check = checkParams(value);
  const names = 'names' in check ? check.names : [];
  if (draft !== undefined)
    return (
      <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
        <ParamsField value={draft} onChange={setDraft} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size="sm"
            variant="primary"
            disabled={!paramsReady(draft)}
            onClick={() => {
              onSave(draft ?? '');
              setDraft(undefined);
            }}
          >
            Salva
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDraft(undefined)}>
            Annulla
          </Button>
        </div>
      </div>
    );
  return (
    <div className="conn-users">
      {names.length ? `Parametri: ${names.join(', ')}` : 'Nessuno schema dei parametri'}
      <button className="conn-link" onClick={() => setDraft(value || null)}>
        Modifica
      </button>
    </div>
  );
}
